import React, { useCallback, useEffect, useState } from "react";
import { Box, CircularProgress, Grid } from "@mui/material";
import ENV from "../lib/env";
import MessageAlert from "./MessageAlert";
import styles from "../styles/MintContent.module.css";
import Title from "./Title";
import CountdownClock from "./CountdownClock";
import DownArrow from "./DownArrow";
import MintForm from "./MintForm";
import MintTimeline from "./MintTimeline";
import isNil from "lodash/isNil";
import { MintingWindowState, MintWindowParams } from "../types/mint";
import MintTitle from "./MintTitle";
import MintArtwork from "./MintArtwork";
import MintCountdownClock from "./MintCountdownClock";
import { ethers } from "ethers";

interface Props {}

export interface IAlertState {
  isOpen: boolean;
  closeAlert: any;
  message: string;
  handleClose: any;
  vertical: "top" | "bottom";
  horizontal: "left" | "center" | "right";
  severity: any;
}

export const initialAlertState: IAlertState = {
  isOpen: false,
  closeAlert: null,
  message: "",
  handleClose: null,
  vertical: "top",
  horizontal: "center",
  severity: null,
};

const MINTING_WINDOW_PARAMS = {
  [MintingWindowState.BEFORE_MINT]: {
    title: "Mint",
    nextMint: "Mint",
    state: MintingWindowState.BEFORE_MINT,
    nextMintOpensAt: ENV.OG_WINDOW_START_DATETIME,
    basePrice: ethers.utils.parseEther("0.07"),
  } as MintWindowParams,
  [MintingWindowState.OG_MINT]: {
    title: "Mint",
    nextMint: "Mint",
    state: MintingWindowState.OG_MINT,
    nextMintOpensAt: ENV.PUBLIC_START_DATETIME,
    basePrice: ethers.utils.parseEther("0.07"),
  } as MintWindowParams,
  [MintingWindowState.PUBLIC_MINT]: {
    title: "Public Mint",
    nextMint: null,
    state: MintingWindowState.PUBLIC_MINT,
    nextMintOpensAt: null,
    basePrice: ethers.utils.parseEther("0.15"),
  } as MintWindowParams,
};

const MintContent = (props: Props) => {
  const [alertState, setAlertState] = useState<IAlertState>(initialAlertState);
  const [mintWindowParams, setMintWindowParams] =
    useState<MintWindowParams | null>(null);
  const [nftsMinted, setNftsMinted] = useState<number | null>(null);
  const closeAlert = useCallback(() => {
    setAlertState({ ...alertState, isOpen: false });
  }, [setAlertState, alertState]);

  const handleToTimelineClick = (event: React.MouseEvent) => {
    document?.getElementById("timeline")?.scrollIntoView(true);
  };

  useEffect(() => {
    const now = Date.now();

    let state;
    if (now < ENV.OG_WINDOW_START_DATETIME) {
      state = MintingWindowState.BEFORE_MINT;
    } else if (
      ENV.OG_WINDOW_START_DATETIME <= now &&
      now < ENV.PUBLIC_START_DATETIME
    ) {
      state = MintingWindowState.OG_MINT;
    } else {
      state = MintingWindowState.PUBLIC_MINT;
    }

    setMintWindowParams(MINTING_WINDOW_PARAMS[state]);
  }, [setMintWindowParams]);

  if (isNil(mintWindowParams)) {
    return <CircularProgress />;
  }

  const isBeforeMint =
    mintWindowParams.state === MintingWindowState.BEFORE_MINT;
  const isWhitelistMint = mintWindowParams.state === MintingWindowState.OG_MINT;

  return (
    <Box mt="80px">
      <div className={styles.container}>
        <Grid
          className={styles.containerImage}
          container
          spacing={1}
          direction="column"
          justifyContent="center"
          alignItems="center"
          sx={{ marginTop: "6vh" }}
          px={5}
          key={"whitelist-section"}
        >
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            direction="column"
            className={styles.mintSection}
          >
            <Grid item sx={{ textAlign: "center" }}>
              <Box mt={10}>
                <Title text={mintWindowParams.title} />
              </Box>

              <MintTitle mintWindowParams={mintWindowParams} />

              {isBeforeMint ? (
                <CountdownClock
                  dateTime={mintWindowParams.nextMintOpensAt as number}
                  isCompact={false}
                />
              ) : (
                <MintArtwork nftsMinted={nftsMinted} />
              )}

              <MintForm
                setAlertState={setAlertState}
                closeAlert={closeAlert}
                mintWindowParams={mintWindowParams}
                setNftsMinted={setNftsMinted}
                nftsMinted={nftsMinted}
              />

              {isWhitelistMint && (
                <MintCountdownClock mintWindowParams={mintWindowParams} />
              )}

              <Box mb={5} mt={12}>
                <div id={"to-timeline"} onClick={handleToTimelineClick}>
                  <p>Mint timeline</p>
                  <DownArrow />
                </div>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </div>
      <MintTimeline mintWindowParams={mintWindowParams} />
      <MessageAlert {...alertState} />
    </Box>
  );
};

export default MintContent;
